import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { UpgradeCTA } from "../components/UpgradeCTA";
import { getPatients, getPatientConsultations } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
  Search, User, Calendar, BookOpen, Loader2, ChevronRight, Stethoscope
} from "lucide-react";
import { isUsClinicalHost } from "../lib/usClinicalHost";

/** Desactivado: POST /api/ai/consultation-summary no está en FastAPI estable. */
const ENABLE_CLINICAL_AI_ASSISTANT = false;

export default function Reports() {
  const isUsHost = isUsClinicalHost();
  const { isBillingExempt, isPremium } = useAuth();
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [docTypeFilter, setDocTypeFilter] = useState("todos");

  useEffect(() => {
    fetchPatients();
  }, []);
  useEffect(() => {
    if (selectedPatient) fetchConsultations(selectedPatient.id);
  }, [selectedPatient]);

  const fetchPatients = async () => {
    try {
      const response = await getPatients();
      setPatients(response.data);
    } catch {
      toast.error(isUsClinicalHost() ? "Unable to load patients." : "Error al cargar pacientes");
    } finally {
      setLoading(false);
    }
  };

  const fetchConsultations = async (patientId) => {
    try {
      const response = await getPatientConsultations(patientId);
      setConsultations(response.data || []);
    } catch {
      setConsultations([]);
    }
  };

  const filteredPatients = patients.filter(p =>
    `${p.nombres} ${p.apellidos}`.toLowerCase().includes(searchTerm.toLowerCase())
  );
  const filteredConsultations = consultations
    .filter((c) => {
      if (docTypeFilter === "todos") return true;
      const value = String(c.tipo_consulta || "").toLowerCase();
      if (docTypeFilter === "primera_vez") return value === "primera vez";
      if (docTypeFilter === "control") return value === "control";
      if (docTypeFilter === "reconsulta") return value === "reconsulta";
      return true;
    })
    .slice(0, 25);

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">
            {isUsHost ? "Visit history & clinical documents" : "Historial de documentos y consultas"}
          </h1>
          <p className="text-slate-500 text-sm">
            {isUsHost
              ? "Find a prior visit and open the generated clinical document."
              : "Busque una consulta previa y abra su documento oficial"}
          </p>
        </div>
      </div>

      {!isBillingExempt && !isPremium && (
        <UpgradeCTA
          heading={
            isUsHost
              ? "Unlock the full workflow with Aurora Geriatric Professional"
              : "Desbloquea funciones completas con Aurora Geriátrica Profesional"
          }
          buttonLabel={isUsHost ? "Upgrade to Pro" : "Actualizar ahora"}
          to="/checkout"
        />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Patient Selection */}
        <Card className="lg:col-span-1 border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <User className="h-4 w-4 text-sky-600" />
              {isUsHost ? "Patient" : "Paciente"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <Input
                placeholder={isUsHost ? "Search…" : "Buscar..."}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-8 h-8 text-sm"
                data-testid="patient-search-input"
              />
            </div>

            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
              </div>
            ) : (
              <div className="max-h-[440px] overflow-y-auto space-y-1.5">
                {filteredPatients.map((patient) => (
                  <button
                    key={patient.id}
                    onClick={() => setSelectedPatient(patient)}
                    className={`w-full px-3 py-2.5 rounded-lg text-left transition-all ${
                      selectedPatient?.id === patient.id
                        ? "bg-sky-100 border border-sky-300 text-sky-800"
                        : "bg-slate-50 hover:bg-slate-100 border border-transparent text-slate-700"
                    }`}
                    data-testid={`select-patient-${patient.id}`}
                  >
                    <p className="font-medium text-sm">{patient.nombres} {patient.apellidos}</p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {patient.edad} {isUsHost ? "yrs" : "años"} ·{" "}
                      {patient.diagnostico_principal || (isUsHost ? "No primary diagnosis" : "Sin diagnóstico")}
                    </p>
                  </button>
                ))}
                {filteredPatients.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-4">
                    {isUsHost ? "No results" : "Sin resultados"}
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Report Generation */}
        <div className="lg:col-span-2 space-y-5">
          {!selectedPatient ? (
            <Card className="border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                <BookOpen className="h-12 w-12 text-slate-300 mb-3" />
                <p className="text-slate-500 font-medium">{isUsHost ? "Select a patient" : "Seleccione un paciente"}</p>
                <p className="text-sm text-slate-400 mt-1">
                  {isUsHost
                    ? "Choose a patient from the list to review visits and open documents."
                    : "Elija un paciente de la lista para ver su historial y abrir documentos"}
                </p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Patient summary banner */}
              <div className="flex items-center justify-between bg-sky-50 border border-sky-200 rounded-xl px-4 py-3">
                <div>
                  <p className="font-semibold text-sky-800">{selectedPatient.nombres} {selectedPatient.apellidos}</p>
                  <p className="text-xs text-sky-600">
                    {selectedPatient.edad} {isUsHost ? "yrs" : "años"} ·{" "}
                    {selectedPatient.diagnostico_principal || (isUsHost ? "No primary diagnosis" : "Sin diagnóstico")}
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-sky-600 hover:bg-sky-100"
                  onClick={() => navigate(`/pacientes/${selectedPatient.id}`)}
                >
                  {isUsHost ? "View profile" : "Ver perfil"} <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>

              {/* Historial de documentos por consulta */}
              <Card className="border-slate-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
                    {isUsHost ? "Visit history" : "Historial de consultas"}
                    <Badge variant="outline" className="text-xs">
                      {consultations.length}{" "}
                      {isUsHost
                        ? `visit${consultations.length !== 1 ? "s" : ""}`
                        : `consulta${consultations.length !== 1 ? "s" : ""}`}
                    </Badge>
                  </CardTitle>
                  <p className="text-xs text-slate-400">
                    {isUsHost
                      ? "One action per visit to open the official clinical document."
                      : "Una acción por consulta para abrir el documento oficial"}
                  </p>
                </CardHeader>
                <CardContent>
                  {consultations.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-sm text-slate-400">
                        {isUsHost ? "This patient has no visits on file." : "Este paciente no tiene consultas registradas"}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-xs text-slate-500">{isUsHost ? "Filter by type:" : "Filtrar por tipo:"}</span>
                        <select
                          value={docTypeFilter}
                          onChange={(e) => setDocTypeFilter(e.target.value)}
                          className="h-8 text-xs border border-slate-200 rounded-md px-2 bg-white text-slate-700"
                        >
                          <option value="todos">{isUsHost ? "All" : "Todos"}</option>
                          <option value="primera_vez">{isUsHost ? "New patient" : "Primera vez"}</option>
                          <option value="control">{isUsHost ? "Follow-up" : "Control"}</option>
                          <option value="reconsulta">{isUsHost ? "Revisit" : "Reconsulta"}</option>
                        </select>
                      </div>
                      <div className="rounded-xl border border-slate-200 overflow-hidden">
                        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-slate-50 text-[11px] font-semibold text-slate-600 uppercase tracking-wide">
                          <div className="col-span-3">{isUsHost ? "Patient" : "Paciente"}</div>
                          <div className="col-span-2">{isUsHost ? "Date" : "Fecha"}</div>
                          <div className="col-span-3">{isUsHost ? "Document type" : "Tipo de documento"}</div>
                          <div className="col-span-2">{isUsHost ? "Clinician" : "Médico"}</div>
                          <div className="col-span-2 text-right">{isUsHost ? "Action" : "Acción"}</div>
                        </div>
                        {filteredConsultations.map((consult) => (
                          <div key={consult.id} className="grid grid-cols-12 gap-2 px-3 py-2.5 border-t border-slate-100 items-center">
                            <div className="col-span-3 text-sm text-slate-700 font-medium truncate">
                              {selectedPatient.nombres} {selectedPatient.apellidos}
                            </div>
                            <div className="col-span-2 text-xs text-slate-600">
                              {new Date(consult.fecha_consulta).toLocaleDateString(isUsHost ? "en-US" : "es-ES")}
                            </div>
                            <div className="col-span-3">
                              <Badge variant="outline" className="text-[11px] border-sky-200 text-sky-700 bg-sky-50">
                                {isUsHost ? "Clinical & family document" : "Documento clínico y familia"}
                              </Badge>
                            </div>
                            <div className="col-span-2 text-xs text-slate-600 truncate">
                              {consult.medico || consult.doctor || (isUsHost ? "Aurora team" : "Equipo Aurora")}
                            </div>
                            <div className="col-span-2 flex justify-end">
                              <Button
                                size="sm"
                                className="h-8 text-xs bg-sky-600 hover:bg-sky-700"
                                onClick={() => navigate(`/entregable/${consult.id}`)}
                              >
                                {isUsHost ? "Open document" : "Abrir documento"}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                      {filteredConsultations.length === 0 && (
                        <p className="text-sm text-slate-400 text-center py-4">
                          {isUsHost ? "No documents match this filter." : "No hay documentos para este filtro"}
                        </p>
                      )}
                      {consultations.length > 25 && (
                        <p className="text-xs text-slate-400 text-center">
                          {isUsHost ? "Showing 25 most recent records." : "Mostrando 25 registros recientes"}
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
